import { cva } from 'class-variance-authority';
import type { ButtonHTMLAttributes } from 'react';
import { tlwTwMerge, type GenerateCvaType } from '@trg/utils';

export type SpecLabelProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type'
> & {
  type: 'text' | 'color';
  size?: 'small' | 'medium' | 'large';
  active?: boolean;
};

type SpecLabelVariantsType = GenerateCvaType<
  SpecLabelProps,
  'type' | 'size' | 'active'
>;

const SpecLabelVariants = cva<SpecLabelVariantsType>(
  [
    'tlw-border tlw-duration-300 tlw-truncate tlw-select-none',
    'disabled:tlw-text-txt-500 disabled:tlw-bg-white disabled:tlw-border-gray-400',
  ],
  {
    variants: {
      type: {
        text: [
          'tlw-outline-black/15',
          'hover:tlw-text-txt-white hover:tlw-bg-pr hover:tlw-border-pr',
          'active:tlw-text-txt-white active:tlw-bg-pr active:tlw-border-pr',
          'focus-visible:tlw-outline focus-visible:tlw-outline-2',
        ],
        color: ['tlw-p-px tlw-rounded-full tlw-outline-offset-1'],
      },
      size: {
        small: '',
        medium: '',
        large: '',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        type: 'text',
        active: false,
        class: ['tlw-text-txt-700 tlw-bg-white tlw-border-txt-500'],
      },
      {
        type: 'text',
        active: true,
        class: ['tlw-text-txt-white tlw-bg-pr tlw-border-pr'],
      },
      {
        type: 'text',
        size: 'large',
        class: [
          'tlw-txt-zh-head-8 tlw-px-[9px] tlw-py-px tlw-rounded-md lg:tlw-py-[3px]',
        ],
      },
      {
        type: 'text',
        size: 'medium',
        class: [
          'tlw-txt-zh-mini tlw-max-w-[5rem] tlw-px-[7px] tlw-py-[3px] tlw-rounded',
        ],
      },
      {
        type: 'color',
        active: false,
        class: ['tlw-border-transparent hover:tlw-border-gray-400'],
      },
      {
        type: 'color',
        active: true,
        class: ['tlw-border-gray-600'],
      },
    ],
  }
);

export function SpecLabel({
  type,
  size = 'medium',
  active = false,
  className,
  ...props
}: SpecLabelProps) {
  return (
    <button
      className={tlwTwMerge(
        SpecLabelVariants({ type, size, active }),
        className
      )}
      type="button"
      {...props}
    />
  );
}
